import { CourierClient } from '@trycourier/courier-js';

import type {
  CourierPreferenceTopic,
  InboxNotification,
  NewTransactionsDigestData,
} from '@/types/notifications';

// The client talks to Courier DIRECTLY with the short-lived, user-scoped JWT issued by
// our API (inbox + preferences scopes). Courier enforces per-user isolation through the
// token itself, which is why no proxy endpoints exist — and why this module must never
// see any credential other than that JWT.
const COURIER_API = 'https://api.courier.com';

export interface InboxSnapshot {
  notifications: InboxNotification[];
  /** The vendor's total unread count when present — the page of nodes is capped, so a
   * derived count undercounts once older unread digests fall off the first page. */
  unreadCount: number;
}

export function createInboxClient(userId: string, jwt: string): CourierClient {
  return new CourierClient({ userId, jwt });
}

export async function fetchInboxSnapshot(client: CourierClient): Promise<InboxSnapshot> {
  const response = await client.inbox.getMessages();
  return mapInboxResponse(response);
}

export async function markMessageRead(client: CourierClient, messageId: string): Promise<void> {
  await client.inbox.read({ messageId });
}

export async function markAllRead(client: CourierClient): Promise<void> {
  await client.inbox.readAll();
}

/**
 * Defensive mapping over the vendor SDK response: the nodes live at
 * `data.messages.nodes` today, but nothing here may throw on a shape drift —
 * an unreadable inbox must degrade to "no notifications", never a crashed header.
 * The renderable surface is the vendor-RENDERED fields (title, preview, actions):
 * verified against production 2026-08-28, the inbox API returns `data: {}` for
 * template sends, so the raw digest is progressive enhancement only. Nodes without a
 * usable title are dropped; everything else renders through its own action.
 */
export function mapInboxResponse(response: unknown): InboxSnapshot {
  const root = response as {
    data?: { messages?: { nodes?: unknown }; unreadCount?: unknown };
    messages?: { nodes?: unknown };
    unreadCount?: unknown;
  } | null;
  const rawNodes = root?.data?.messages?.nodes ?? root?.messages?.nodes;
  const nodes = Array.isArray(rawNodes) ? rawNodes : [];
  const notifications = nodes.flatMap((node): InboxNotification[] => {
    // Every field that reaches a render is runtime-validated — a drifted node must
    // degrade (fallback or drop), never hand React a non-string to print.
    const message = node as {
      messageId?: unknown;
      title?: unknown;
      read?: unknown;
      created?: unknown;
      data?: unknown;
    } | null;
    if (typeof message?.messageId !== 'string' || message.messageId.length === 0) return [];
    if (typeof message.title !== 'string' || message.title.length === 0) return [];
    const action = mapAction((message as { actions?: unknown }).actions);
    return [
      {
        messageId: message.messageId,
        title: message.title,
        preview:
          typeof (message as { preview?: unknown }).preview === 'string' &&
          ((message as { preview?: string }).preview ?? '').length > 0
            ? ((message as { preview?: string }).preview as string)
            : null,
        actionLabel: action?.label ?? null,
        actionHref: action?.href ?? null,
        read: Boolean(message.read),
        createdAt: typeof message.created === 'string' ? message.created : null,
        digest: mapDigestData(message.data),
      },
    ];
  });

  const vendorUnread = root?.data?.unreadCount ?? root?.unreadCount;
  return {
    notifications,
    unreadCount:
      typeof vendorUnread === 'number' && vendorUnread >= 0
        ? vendorUnread
        : countUnread(notifications),
  };
}

function mapAction(actions: unknown): { label: string | null; href: string | null } | null {
  if (!Array.isArray(actions) || actions.length === 0) return null;
  const first = actions[0] as { content?: unknown; href?: unknown } | null;
  if (!first) return null;
  return {
    label: typeof first.content === 'string' && first.content.length > 0 ? first.content : null,
    href: typeof first.href === 'string' && first.href.length > 0 ? first.href : null,
  };
}

export function mapDigestData(data: unknown): NewTransactionsDigestData | null {
  const digest = data as Partial<NewTransactionsDigestData> | null;
  if (!digest || typeof digest.total !== 'number') return null;
  return {
    total: digest.total,
    institutions: Array.isArray(digest.institutions)
      ? digest.institutions.filter((i) => typeof i?.name === 'string' && typeof i?.count === 'number')
      : [],
    sample: Array.isArray(digest.sample)
      ? digest.sample.filter(
          (s) =>
            typeof s?.name === 'string' &&
            typeof s?.amount === 'string' &&
            (s?.direction === 'income' || s?.direction === 'expense'),
        )
      : [],
    syncedAt: typeof digest.syncedAt === 'string' ? digest.syncedAt : '',
  };
}

export function countUnread(notifications: InboxNotification[]): number {
  return notifications.filter((n) => !n.read).length;
}

// --- Preferences (Courier REST with the same user JWT) --------------------------------

export async function fetchPreferenceTopics(
  userId: string,
  jwt: string,
): Promise<CourierPreferenceTopic[]> {
  const response = await fetch(
    `${COURIER_API}/users/${encodeURIComponent(userId)}/preferences`,
    { headers: { Authorization: `Bearer ${jwt}` } },
  );
  if (!response.ok) throw new Error(`Courier preferences failed: ${response.status}`);
  const body = (await response.json()) as { items?: CourierPreferenceTopic[] };
  return body.items ?? [];
}

/** Courier's PUT replaces the whole topic object — if any surface ever writes
 * `custom_routing`/snooze for a topic, this status-only body would wipe it and must
 * start round-tripping those fields. Today this is the only writer. */
export async function setPreferenceTopic(
  userId: string,
  jwt: string,
  topicId: string,
  optedIn: boolean,
): Promise<void> {
  const response = await fetch(
    `${COURIER_API}/users/${encodeURIComponent(userId)}/preferences/${encodeURIComponent(topicId)}`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ topic: { status: optedIn ? 'OPTED_IN' : 'OPTED_OUT' } }),
    },
  );
  if (!response.ok) throw new Error(`Courier preference update failed: ${response.status}`);
}

/** Maps a rendered action href onto an in-app route; external/foreign hosts return null. */
export function inAppPath(href: string | null): string | null {
  if (!href) return null;
  try {
    // Everything goes through URL against the page origin — a bare "/path" resolves
    // same-origin, while a protocol-relative "//foreign.example/path" resolves to the
    // foreign host and fails the allowlist (no string-prefix shortcuts).
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin || url.hostname === 'app.vertice.finance') {
      // Collapse leading slashes: an allowlisted URL like
      // https://app.vertice.finance//evil.example/x has pathname "//evil.example/x",
      // which navigation would reinterpret as protocol-relative (cross-origin again).
      return url.pathname.replace(/^\/+/, '/') + url.search;
    }
  } catch {
    // Malformed href — fall back.
  }
  return null;
}
